import { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { getGoogleOAuthClientId } from './config/oauth';
import { StyledEngineProvider } from '@mui/material/styles';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NewsUpdatesProvider } from './contexts/NewsUpdatesContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext';
import HomePage from './pages/HomePage';
import MyProfilePage from './pages/MyProfilePage';
import SearchPage from './pages/SearchPage';
import BookmarksPage from './pages/BookmarksPage';
import FolderDetailPage from './pages/FolderDetailPage';
import SettingsPage from './pages/SettingsPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import AdvancedSearchPage from './pages/AdvancedSearchPage';
import VisiCreatePage from './pages/VisiCreatePage';
import SubscriptionSettingsPage from './pages/SubscriptionSettingsPage';
import NotificationsListPage from './pages/NotificationsListPage';
import MapsSettingsPage from './pages/MapsSettingsPage';
import NearbyMapPage from './pages/NearbyMapPage';
import LocationSettingsPage from './pages/LocationSettingsPage';
import LanguageSettingsPage from './pages/LanguageSettingsPage';
import AppearanceSettingsPage from './pages/AppearanceSettingsPage';
import PrivacyCenterPage from './pages/PrivacyCenterPage';
import ReportProblemPage from './pages/ReportProblemPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import QrCodePage from './pages/QrCodePage';
import VisiEditHubPage from './pages/VisiEditHubPage';
import VisiWizardPage from './pages/VisiWizardPage';
import VisiInfoPage from './pages/VisiInfoPage';
import BusinessProfilePage from './pages/BusinessProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import PresentationPage from './pages/PresentationPage';
import LandingPage from './pages/LandingPage';
import GuestEntry from './pages/GuestEntry';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { createAppTheme } from './theme/createAppTheme';
import { isFeatureEnabled } from './config/featureFlags';
import { normalizePublicUsername } from './utils/publicProfileUrl';
import { marketingRoutes } from './marketing/MarketingRoutes';
import { MARKETING_RESERVED_SLUGS } from './marketing/marketingNav';
import { Sentry } from './sentry';
import { useNoIndexPageMeta } from './marketing/useMarketingPageMeta';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

function AuthenticatedShellSeo() {
  const { pathname } = useLocation();
  useNoIndexPageMeta('VisiMe', pathname);
  return null;
}

/** Single-segment paths that must not be treated as public VISI usernames for guests. */
const GUEST_RESERVED_TOP_LEVEL = new Set([
  'homepage',
  'home',
  'login',
  'forgot-password',
  'register',
  'profile',
  'me',
  'search',
  'bookmarks',
  'map',
  'settings',
  'notifications',
  'presentation',
  'visi-info',
  'visi',
  'create',
  'auth',
  'api',
  'download',
  'admin',
  ...MARKETING_RESERVED_SLUGS,
]);

function GuestPublicProfile() {
  const { id } = useParams<{ id: string }>();
  const username = normalizePublicUsername(id);
  if (!username || GUEST_RESERVED_TOP_LEVEL.has(username.toLowerCase())) {
    return <Navigate to="/" replace />;
  }
  return <BusinessProfilePage />;
}

function GuestPublicProfileByProfilePath() {
  const { username } = useParams<{ username: string }>();
  const normalized = normalizePublicUsername(username);
  if (!normalized || GUEST_RESERVED_TOP_LEVEL.has(normalized.toLowerCase())) {
    return <Navigate to="/" replace />;
  }
  return <BusinessProfilePage />;
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();
  const marketingExperienceEnabled = isFeatureEnabled('webMarketingExperience');

  if (isLoading) {
    return null; // Loading state handled by ProtectedRoute
  }

  if (!isAuthenticated) {
    return (
      <SentryRoutes>
        <Route path="/" element={<GuestEntry />} />
        {marketingRoutes(marketingExperienceEnabled)}
        <Route path="/profile/:username" element={<GuestPublicProfileByProfilePath />} />
        {/* Public VISI profiles must stay reachable for QR / deep links (parity with iOS visi.me). */}
        <Route path="/:id" element={<GuestPublicProfile />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </SentryRoutes>
    );
  }

  return (
    <>
      <AuthenticatedShellSeo />
      <SentryRoutes>
      <Route
        path="/presentation"
        element={marketingExperienceEnabled ? <PresentationPage /> : <Navigate to="/homepage" replace />}
      />
      <Route path="/login" element={<Navigate to="/homepage" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      <Route
        path="/"
        element={marketingExperienceEnabled ? <LandingPage /> : <Navigate to="/homepage" replace />}
      />
      {marketingRoutes(marketingExperienceEnabled)}

      <Route path="/home" element={<Navigate to="/homepage" replace />} />

      <Route
        path="/homepage"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MyProfilePage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/search" 
        element={
          <ProtectedRoute>
            <SearchPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/search/advanced"
        element={
          <ProtectedRoute>
            <AdvancedSearchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visi-info"
        element={
          <ProtectedRoute>
            <VisiInfoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visi/create"
        element={
          <ProtectedRoute>
            <VisiCreatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visi/edit/:businessId"
        element={
          <ProtectedRoute>
            <VisiEditHubPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visi/edit/:businessId/wizard"
        element={
          <ProtectedRoute>
            <VisiWizardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visi/qr/:username"
        element={
          <ProtectedRoute>
            <QrCodePage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/bookmarks" 
        element={
          <ProtectedRoute>
            <BookmarksPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/bookmarks/:id"
        element={
          <ProtectedRoute>
            <FolderDetailPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/settings/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/map"
        element={
          <ProtectedRoute>
            <NearbyMapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/maps"
        element={
          <ProtectedRoute>
            <MapsSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/location"
        element={
          <ProtectedRoute>
            <LocationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/language"
        element={
          <ProtectedRoute>
            <LanguageSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/appearance"
        element={
          <ProtectedRoute>
            <AppearanceSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/privacy"
        element={
          <ProtectedRoute>
            <PrivacyCenterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/report"
        element={
          <ProtectedRoute>
            <ReportProblemPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />

      {/* Business profile route - catch-all at the end, so specific routes take precedence */}
      <Route path="/profile/:username" element={<BusinessProfilePage />} />
      <Route 
        path="/:id" 
        element={<BusinessProfilePage />}
      />
      
      <Route path="*" element={<Navigate to="/homepage" replace />} />
      </SentryRoutes>
    </>
  );
}

function ThemedApp() {
  const { mode } = useThemeMode();
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <NewsUpdatesProvider>
            <AppRoutes />
          </NewsUpdatesProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

function AppShell() {
  const googleClientId = getGoogleOAuthClientId();
  if (!googleClientId) {
    return <ThemedApp />;
  }
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <ThemedApp />
    </GoogleOAuthProvider>
  );
}

export default function App() {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeModeProvider>
        <AppShell />
      </ThemeModeProvider>
    </StyledEngineProvider>
  );
}
